import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "../lib/api";
import { NewsletterForm } from "../components/Footer";
import { ProductCard } from "../components/ProductCard";
import { ShareButtons } from "../components/ShareButtons";

export function FieldGuide() {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("");
  useEffect(() => { api.get(`/blog${category ? `?category=${encodeURIComponent(category)}` : ""}`).then(({ data }) => setPosts(data)).catch(() => {}); }, [category]);
  const cats = ["Preparedness", "Personal Security", "Tactical Gear", "Everyday Carry", "Vehicle Preparedness"];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-14">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#4A5D23] font-mono">Content & Education</p>
      <h1 className="font-heading text-4xl md:text-5xl uppercase font-black tracking-tighter mt-2">The Shodan Field Guide</h1>
      <p className="text-black/60 mt-3 max-w-xl">Practical knowledge for prepared people. No fear — just planning.</p>
      <div className="flex flex-wrap gap-2 mt-8">
        <button onClick={() => setCategory("")} className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-colors duration-150 ${!category ? "bg-[#121212] text-white border-black" : "border-black/20 hover:border-black"}`}>All</button>
        {cats.map((c) => (
          <button key={c} data-testid={`blog-cat-${c.toLowerCase().replace(/ /g, "-")}`} onClick={() => setCategory(c)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-colors duration-150 ${category === c ? "bg-[#121212] text-white border-black" : "border-black/20 hover:border-black"}`}>{c}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
        {posts.map((post) => (
          <Link key={post.slug} data-testid={`article-card-${post.slug}`} to={`/field-guide/${post.slug}`} className="group bg-white border border-black/10 transition-transform duration-150 hover:-translate-y-0.5">
            <div className="aspect-[16/9] overflow-hidden bg-[#F4F3F0]">
              <img src={post.image} alt={post.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
            </div>
            <div className="p-5">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#4A5D23]">{post.category}</span>
                <span className="text-[10px] text-black/40 font-mono">{post.read_time} min read</span>
              </div>
              <h2 className="font-heading font-bold tracking-tight mt-2 leading-snug group-hover:text-[#4A5D23] transition-colors duration-150">{post.title}</h2>
              <p className="text-xs text-black/50 mt-2 line-clamp-2">{post.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function Article() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [gear, setGear] = useState([]);
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setPost(false));
    api.get(`/blog/${slug}/products`).then(({ data }) => setGear(data)).catch(() => setGear([]));
  }, [slug]);

  if (post === false) return <div className="max-w-3xl mx-auto px-4 py-32 text-center text-black/50">Article not found.</div>;
  if (!post) return <div className="max-w-3xl mx-auto px-4 py-32 text-center text-black/40 animate-pulse">Loading…</div>;

  return (
    <article className="max-w-3xl mx-auto px-4 md:px-8 py-14">
      <Link to="/field-guide" className="text-xs font-bold uppercase tracking-widest text-black/50 hover:text-black">← Field Guide</Link>
      <span className="block text-[10px] font-bold uppercase tracking-widest text-[#4A5D23] mt-6">{post.category}</span>
      <h1 data-testid="article-title" className="font-heading text-3xl md:text-4xl uppercase font-black tracking-tighter mt-2 leading-tight">{post.title}</h1>
      <p className="text-xs text-black/40 mt-3 font-mono">{post.author} · {post.published_at} · {post.read_time} min read</p>
      <div className="mt-4">
        <ShareButtons title={post.title} />
      </div>
      <div className="aspect-[16/9] overflow-hidden mt-8 border border-black/10">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
      </div>
      <div data-testid="article-content" className="mt-8 space-y-4 text-black/75 leading-relaxed">
        {post.content.split("\n\n").map((block, i) => {
          if (block.startsWith("## ")) return <h2 key={i} className="font-heading text-xl uppercase font-bold tracking-tight text-black pt-4">{block.slice(3)}</h2>;
          const lines = block.split("\n");
          if (lines.every((l) => l.trim().startsWith("- "))) {
            return <ul key={i} className="list-disc pl-5 space-y-1">{lines.map((l, j) => <li key={j} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(l.trim().slice(2).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")) }} />)}</ul>;
          }
          return <p key={i} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")) }} />;
        })}
      </div>
      {gear.length > 0 && (
        <div data-testid="article-gear-section" className="mt-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4A5D23] font-mono">Shop This Guide</p>
          <h3 className="font-heading text-xl uppercase font-bold tracking-tight mt-1 mb-5">Gear From This Guide</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {gear.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        </div>
      )}
      <div className="mt-12 border border-black/10 bg-[#F4F3F0] p-8">
        <h3 className="font-heading text-lg uppercase font-bold tracking-tight">Get the Free Preparedness Checklist</h3>
        <p className="text-xs text-black/50 mt-1 mb-4">Everything in this guide, in a printable one-pager.</p>
        <NewsletterForm source="field_guide" />
      </div>
      <div className="mt-8 flex gap-4">
        <Link to="/shop?category=preparedness" className="bg-[#121212] text-white px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">Shop Preparedness Gear</Link>
      </div>
    </article>
  );
}
